import PropTypes from 'prop-types';
import React from 'react';
import ReactRouterPropTypes from 'react-router-prop-types';

import { Link } from 'react-router-dom';
import { useTable, useSortBy, usePagination, useGlobalFilter } from 'react-table';

import axios from 'axios';
import { isEmpty } from 'underscore';

import { READ, WRITE } from './Om7Sense';
import { ClassifierPicker } from "./ClassifierPicker.jsx";
import { ReportingPageTabs } from './ReportingComponents/ReportingPageTabs.js';
import { Pagination } from "./util/tables/Pagination";
import { SearchableTable } from "./util/tables/SearchableTable";


function Table({ columns, data }) {
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state,
        visibleColumns,
        setGlobalFilter,
    } = useTable({
        columns,
        data,
        initialState: {
            pageSize: 20,
            sortBy: [{id: 'name', desc: true}],
        }
    }, useGlobalFilter, useSortBy, usePagination);

    return (
        <React.Fragment>
            <SearchableTable tableProps={getTableProps()} globalFilter={state.globalFilter} setGlobalFilter={setGlobalFilter}
                             state={ state }  columns={columns} visibleColumns={visibleColumns} headerGroups={headerGroups}
                             prepareRow={ prepareRow } getTableBodyProps={ getTableBodyProps } page={ page } />
            <Pagination page={ page } pageCount={ pageCount } pageOptions={ pageOptions }
                        canPreviousPage={ canPreviousPage } canNextPage={ canNextPage } gotoPage={ gotoPage }
                        previousPage={ previousPage } nextPage={ nextPage } setPageSize={ setPageSize }
                        pageSize={ state.pageSize } pageIndex={ state.pageIndex } />
        </React.Fragment>
    );
}

const openCreateTask = (reportId, history, reportName) => {
    history.push(`/settings/scheduler/create/${reportId}?name=${encodeURIComponent(reportName)}`);
}

export const openReport = (reportId, timeStart = undefined, isThreshold = false) => {
    const today = new Date();
    let startDate = new Date(), endDate = null;
    startDate.setUTCFullYear(today.getFullYear());
    startDate.setUTCMonth(timeStart === "lastMonth" ? today.getMonth() - 1 : today.getMonth());
    startDate.setUTCDate(1);
    startDate.setUTCHours(0, 0, 0, 0);
    if (timeStart === "lastMonth") {
        endDate = new Date(startDate);
        endDate.setUTCMonth(startDate.getMonth() - 1);
    }

    let url = `/reporting.html?id=${encodeURIComponent(reportId)}&isThreshold=${isThreshold}&start=${startDate.toISOString()}`;
    if (endDate) {
        url += "&end=" + endDate.toISOString();
    }

    window.open(url, "_blank");
}

export class ReportingPage extends React.Component {
    constructor(props, context) {
        super(props, context);
        const t = context.t;
        this.columns = [
            {
                Header: t("reporting.name"),
                id: 'name',
                accessor: 'name',
            }, {
                id: 'description',
                Header: t("general.description"),
                accessor: row => {
                    const topicChips = [];
                    for (let idx in row.descriptions) {
                        const description = row.descriptions[idx];
        
                        if (topicChips.length < 10) {
                            const chip = (
                                <React.Fragment>
                                    <figure className="avatar avatar-sm" data-initial={description.unit} style={{backgroundColor: "#5755d9"}} />&nbsp;
                                    { description.translation }&ensp;
                                </React.Fragment>
                            );
                            topicChips.push(chip);
                        }
                    }
                    return topicChips;
                },
            }, {
                Header: t("settings.header_actions"),
                id: 'actions',
                className: "name",
                accessor: (row) => (
                    <span className="nobreak">
                        <button className="btn btn-primary" onClick={() => openReport(row.id, this.state.timeStart)}>{t("reporting.generate")}</button>&nbsp;
                        <button className="btn btn-primary" onClick={this.deleteReport.bind(self, row.id, row.name)}>{t("general.delete")}</button>&nbsp;
                        <button className="btn btn-primary" onClick={() => openCreateTask(row.id, props.history, row.name)}>{t("reporting.schedule_email")}</button>
                    </span>
                )
            }
        ];
    }
    static contextTypes = {
        t: PropTypes.func.isRequired,
        p: PropTypes.object.isRequired
    }

    static propTypes = {
        history: ReactRouterPropTypes.history.isRequired,
        location: ReactRouterPropTypes.location.isRequired,
        match: ReactRouterPropTypes.match.isRequired
    }

    state = {
        loading: true,
        error: null,
        timeStart: 'thisMonth',
        reportSaveState: null,
        readOnly: !this.context.p.checkPermission(WRITE, "reports"),
        permission: this.context.p.checkPermission(READ, "reports"),
        reports: [],
    };

    _subscriptions = {}; 
    descriptions = {};


    componentWillUnmount() {
        this._isMounted = false;
        for (let i in this._subscriptions) {
            this._subscriptions[i]();
        }
        this._subscriptions = [];
    }

    componentDidMount() {
        this._isMounted = true;
        const self = this;

        this.context.p.subscribe("reports/+", this.updateCallback, true).then((sub) => self._subscriptions["reports/+"] = sub,
            (error) => {
                self.setState({error});
            });

    }

    fetchDescriptions = (rules) => {
        const self = this;
        axios.post('/apiv2/classifier/describe', {paths: rules}).then((response) =>  {
            if (!self._isMounted || !response || !response.data || isEmpty(response.data)) {
                self.setState({
                    loading: false
                });
            } else {
                self.descriptions = Object.assign(self.descriptions, response.data);
                self.updateCallback();
            }
        });
    }


    updateCallback = () => {
        const self = this;

        let topicsThatLackDescriptions = [];
        const reportTopic = this.context.p.DS.reports || [];
        if (!reportTopic) {
            return;
        }
        // Check which descriptions are missing
        const transformedReports = [];
        for (const reportIdx in reportTopic) {
            const report = reportTopic[reportIdx];
            if (typeof report !== 'object' || !report) {
                continue;
            }
            const descriptions = [];
            for (const topic of report.topics) {
                const desc = self.descriptions[topic];
                if (!desc) {
                    topicsThatLackDescriptions.push(topic);
                } else {
                    descriptions.push(desc);
                }
            }
            transformedReports.push(Object.assign({ descriptions, id: reportIdx}, report));
        }

        if (!isEmpty(topicsThatLackDescriptions)) {
            // Fetch descriptions asynchronously and set them in state later
            this.fetchDescriptions(topicsThatLackDescriptions);
        } else {
            this.setState({loading: false, reports: transformedReports });
        }
    }

    handleMultiChange = (uuid, selectedPlans) => {
        console.log("update", JSON.stringify(selectedPlans));
        const update = {uuidplans: Object.assign({}, this.state.uuidplans)};
        const planList = [];
        for (let idx in selectedPlans) {
            planList[idx] = selectedPlans[idx].value;
        }
        update.uuidplans[uuid] = planList;
        this.setState(update);
    }

    deleteReport = (reportId, name) => {
        const self = this;
        if (window.confirm(this.context.t("reporting.confirm_delete_report__", {reportTitle: name}))) {
            this.context.p.put("reports/" + reportId, {delete: true}).then((res) => {
                console.log("deleted", res);
            }, (err) => {
                console.log("Cannot delete", err);
                window.alert(self.context.t("reporting.error_deleting"));
            });
        }
    }

    createReport = (classifier, name) => {
        console.log("createReport", classifier, name);
        const self = this, t = this.context.t;

        const data = { name, topics: [classifier] };

        if (self.toastTimeout) {
            clearTimeout(self.toastTimeout);
        }

        self.toastTimeout = null;
        self.setState({
            saving: true,
            reportSaveState: null
        });

        function setToastTimeout () {
            if (self.toastTimeout) {
                clearTimeout(self.toastTimeout);
            }
            self.toastTimeout = setTimeout(() => {
                self.setState({reportSaveState: null})
                self.toastTimeout = null;
            }, 4000);
        }

        this.context.p.put("/reports", data).then(() => {
            self.setState({
                reportSaveState: {
                    success: true,
                    msg: t("reporting.report__created", {reportTitle: name})}});
            setToastTimeout();
            self.props.history.replace("/reporting");
        }, (err) => {
            console.warn("cannot create report", err);
            self.setState({
                reportSaveState: {
                    success: false,
                    msg: t("reporting.report__creation_error__", {reportTitle: name, error: err})}});
            setToastTimeout();
        });
    }

    addTopicTopReport = (classifier, reportId) => {
        console.log("addTopicTopReport", classifier, reportId);
    }

    render() {
        const self = this, t = this.context.t;

        if (!this.state.permission) {
            return (
                <div className="empty">
                    <div className="empty-icon">
                        <i className="icon icon-people"></i>
                    </div>
                    <p className="empty-title h5">{ t("reporting.no_permission") }</p>
                </div>
            );
        }

        if (this.props.match.params.cmd === "add") {
            return (
                <ClassifierPicker onComplete={(classifier) => this.addTopicTopReport(classifier, this.props.match.params.id)} />
            );
        }

        if (this.props.match.params.cmd === "new") {
            return (
                <ClassifierPicker onComplete={this.createReport} oneNewItemType={t("reporting.one_new_item_type")}
                    thisItemType={t("reporting.this_item_type")} saveButtonText={t("reporting.create_report")} />
            );
        }

        if (this.state.loading) {
            return <div className="loader-element">
                <div className="loading loading-lg"></div>
            </div>;
        }

        let reportToast = null;
        function clearToast() {
            if (self.toastTimeout) {
                self.clearTimeout(self.toastTimeout);
            }
            self.toastTimeout = null;
            self.setState({reportSaveState: null});
        }

        if (self.state.reportSaveState) {
            let classNames = "toast " + (self.state.reportSaveState.success ? "toast-success" : "toast-error");

            reportToast = (
                <div className={classNames}>
                    <button className="btn btn-clear float-right" onClick={clearToast}></button>
                    {self.state.reportSaveState.msg}
                </div>);

        }

        const reports = this.state.reports;

        return (
            <React.Fragment>
                <ReportingPageTabs location={this.props.location} />

                <div className="sidebar">
                    {reportToast}
                    { !this.state.readOnly && (
                        <div className="sidebar__sorting active">
                            <div className="siderbar__sorting__container">
                                <Link className="btn btn-primary" to="/reporting/new">{t('reporting.create_new')}</Link>
                            </div>
                        </div>
                    )}
                </div>

                <div className="content overview reports classic">
                    { isEmpty(reports) ? (
                        <div className="empty">
                            <div className="empty-icon">
                                <i className="icon icon-people"></i>
                            </div>
                            <p className="empty-title h5">{ t("reporting.no_reports") }</p>
                            { !this.state.readOnly && (
                                <div className="empty-action">
                                    <Link className="btn btn-primary" to="/reporting/new">{t('reporting.create_new')}</Link>
                                </div>
                            )}
                        </div>
                    ) : (
                        <Table columns={this.columns} data={reports} t={t} />
                    )}
                </div>

                
                <div className="overview__footer">
                    <a href="#">{t('general.return_to_top')}</a>
                </div>
            </React.Fragment>
        );

    }
}
